import React, { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Bug, Lightbulb, HelpCircle, Loader2, CheckCircle, Camera } from "lucide-react";
import { useAuth } from "utilities/auth";
import { useLocation } from "react-router-dom";
import { client } from "constants/api";

const TYPES = [
  { value: "bug", label: "Bug Report", icon: Bug, color: "text-destructive" },
  { value: "feature", label: "Feature Request", icon: Lightbulb, color: "text-warning" },
  { value: "question", label: "Question", icon: HelpCircle, color: "text-info" },
];

const PRIORITIES = ["Low", "Medium", "High"];

export default function BugNub() {
  const { user } = useAuth();
  const location = useLocation();
  const fileInputRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // { issueKey, issueUrl }
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    type: "bug",
    title: "",
    description: "",
    priority: "Medium",
  });
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);

  const reset = () => {
    setForm({ type: "bug", title: "", description: "", priority: "Medium" });
    setScreenshot(null);
    setScreenshotPreview(null);
    setSubmitted(null);
    setError(null);
  };

  const handleClose = () => {
    setOpen(false);
    // Delay reset so closing animation completes
    setTimeout(reset, 200);
  };

  const handleScreenshot = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScreenshot(file);
    setScreenshotPreview(URL.createObjectURL(file));
  };

  const removeScreenshot = () => {
    setScreenshot(null);
    setScreenshotPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      setError("Title is required");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("type", form.type);
      formData.append("title", form.title.trim());
      formData.append("description", form.description.trim());
      if (form.type === "bug") {
        formData.append("priority", form.priority);
      }
      formData.append("page", location.pathname);
      formData.append("userAgent", navigator.userAgent);
      formData.append("screenResolution", `${window.screen.width}x${window.screen.height}`);

      if (screenshot) {
        formData.append("screenshot", screenshot);
      }

      const res = await client.post("/feedback", formData, {
        headers: {
          Authorization: user ? `${user.id}.${user.accessToken}` : "",
          CompanyHeader: user?.companyId || "",
        },
      });

      setSubmitted({
        issueKey: res.data.issueKey,
        issueUrl: res.data.issueUrl,
      });
    } catch (err) {
      setError(err.response?.data?.error || "Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedType = TYPES.find((t) => t.value === form.type);

  // Don't render if not authenticated
  if (!user) return null;

  return (
    <>
      {/* SOUND-794: this was a floating pill pinned bottom-right (fixed,
          z-40), and on a phone it sat on top of whatever lived down there —
          the Sign in button, pagination, sticky form footers. It now lives
          in the top bar beside the notification bell, so it can never cover
          page content. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="grid h-9 w-9 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
        aria-label="Send feedback"
        title="Report a bug or request a feature"
      >
        <Bug className="h-5 w-5" />
      </button>

      {/* Modal — portalled to <body>. The trigger sits inside the sticky top
          bar's stacking context, which ranks below the fixed z-50 side nav;
          rendered in place, the dialog would open underneath the nav. */}
      {open && createPortal(
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50">
          <div className="bg-card text-card-foreground rounded-t-xl sm:rounded-xl ring-1 ring-border w-full sm:max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="text-lg font-semibold">
                {submitted ? "Submitted!" : "Send Feedback"}
              </h3>
              <button className="btn btn-ghost btn-xs" onClick={handleClose}>
                <X className="h-4 w-4" />
              </button>
            </div>

            {submitted ? (
              /* Success state */
              <div className="p-6 text-center">
                <CheckCircle className="mx-auto h-12 w-12 text-success mb-3" />
                <p className="text-sm font-medium mb-1">
                  Ticket {submitted.issueKey} created
                </p>
                <p className="text-xs text-muted-foreground mb-4">
                  Thanks for the feedback! We'll look into it.
                </p>
                <div className="flex gap-2 justify-center">
                  <button className="btn btn-ghost btn-sm" onClick={handleClose}>
                    Close
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                      reset();
                    }}
                  >
                    Submit Another
                  </button>
                </div>
              </div>
            ) : (
              /* Form */
              <div className="p-4 space-y-4">
                {/* Type selector */}
                <div className="flex gap-2">
                  {TYPES.map((t) => (
                    <button
                      key={t.value}
                      onClick={() => setForm((f) => ({ ...f, type: t.value }))}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                        form.type === t.value
                          ? "bg-brand/10 text-brand ring-1 ring-brand/30"
                          : "bg-muted/50 text-muted-foreground hover:bg-muted"
                      }`}
                    >
                      <t.icon className={`h-3.5 w-3.5 ${form.type === t.value ? t.color : ""}`} />
                      {t.label}
                    </button>
                  ))}
                </div>

                {/* Title */}
                <div>
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    placeholder={
                      form.type === "bug"
                        ? "What went wrong?"
                        : form.type === "feature"
                        ? "What would you like to see?"
                        : "What's your question?"
                    }
                    value={form.title}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                    autoFocus
                  />
                </div>

                {/* Description */}
                <div>
                  <textarea
                    className="input input-bordered w-full"
                    rows={4}
                    placeholder="Describe in detail..."
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  />
                </div>

                {/* Priority (bugs only) */}
                {form.type === "bug" && (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground block mb-1">
                      Priority
                    </label>
                    <div className="flex gap-2">
                      {PRIORITIES.map((p) => (
                        <button
                          key={p}
                          onClick={() => setForm((f) => ({ ...f, priority: p }))}
                          className={`flex-1 px-3 py-1.5 rounded text-xs font-medium transition-all ${
                            form.priority === p
                              ? p === "High"
                                ? "bg-destructive/10 text-destructive ring-1 ring-destructive/30"
                                : p === "Medium"
                                ? "bg-warning/10 text-warning ring-1 ring-warning/30"
                                : "bg-success/10 text-success ring-1 ring-success/30"
                              : "bg-muted/50 text-muted-foreground hover:bg-muted"
                          }`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Screenshot */}
                <div>
                  {screenshotPreview ? (
                    <div className="relative">
                      <img
                        src={screenshotPreview}
                        alt="Screenshot"
                        className="w-full rounded-lg border border-border max-h-32 object-cover"
                      />
                      <button
                        className="absolute top-1 right-1 bg-foreground/60 text-background rounded-full p-1 hover:bg-foreground/80"
                        onClick={removeScreenshot}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-dashed border-border text-xs text-muted-foreground hover:border-brand/50 hover:text-brand transition-colors"
                    >
                      <Camera className="h-3.5 w-3.5" />
                      Attach Screenshot (optional)
                    </button>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleScreenshot}
                  />
                </div>

                {/* Error */}
                {error && (
                  <p className="text-xs text-destructive">{error}</p>
                )}

                {/* Context info */}
                <p className="text-xs text-muted-foreground">
                  Page: {location.pathname}
                </p>

                {/* Actions */}
                <div className="flex gap-2 pt-1">
                  <button
                    className="btn btn-ghost flex-1"
                    onClick={handleClose}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary flex-1"
                    onClick={handleSubmit}
                    disabled={submitting || !form.title.trim()}
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      "Submit"
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
